import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import logger from 'src/util/logger';

/**
 * AdminMeterList component displays a paginated list of meters with their details
 * @param {object} props - Component props
 * @param {object} props.relay - Relay pagination container props
 * @param {object} props.viewer - GraphQL viewer data containing meters information
 * @returns {React.ReactComponentElement} - AdminMeterList component
 */
function AdminMeterList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.error(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { meters } = viewer;

  const intl = useIntl();

  // Load more button text
  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_no_more.label', defaultMessage: 'No more meters to load' });

  // Page title
  const pageTitle = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.page_title', defaultMessage: 'Enosi Admin - Meters' });

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.breadcrumb.admin', defaultMessage: 'Admin' });
  const breadcrumbMeters = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.breadcrumb.meters', defaultMessage: 'Meters' });

  // Page heading
  const headingMeters = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.heading.meters', defaultMessage: 'Meters' });

  // Table headers
  const headerStatus = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.status', defaultMessage: 'Status' });
  const headerIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.identifier', defaultMessage: 'Identifier' });
  const headerId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.id', defaultMessage: 'ID' });
  const headerExternalIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.external_identifier', defaultMessage: 'External identifier' });
  const headerPrimaryBillingPoint = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.primary_billing_point', defaultMessage: 'Primary Billing Point' });
  const headerProperty = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.property', defaultMessage: 'Property' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbMeters },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headingMeters}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerIdentifier}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">{headerExternalIdentifier}</th>
                <th scope="col">{headerPrimaryBillingPoint}</th>
                <th scope="col">{headerProperty}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                meters && meters.edges && meters.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    active, externalIdentifier, uuid, identifier, primaryBillingPoint, property,
                  } = node;

                  return (
                    <tr key={`admin-meter-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/meters/${uuid}`}>
                          <div>{identifier}</div>
                        </Link>
                        <UUID uuid={uuid} />
                        <br />
                        {`(${externalIdentifier})`}
                      </th>
                      <td>
                        <Link to={`/admin/primary-billing-points/${primaryBillingPoint.uuid}`}>
                          {primaryBillingPoint.identifier}
                        </Link>
                        <br />
                        <UUID uuid={primaryBillingPoint.uuid} />
                        <br />
                        <BadgeActive
                          start={primaryBillingPoint.active.start}
                          finish={primaryBillingPoint.active.finish}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${property.uuid}`}>
                          {property.title}
                        </Link>
                        <br />
                        <UUID uuid={property.uuid} />
                        <br />
                        <BadgeActive
                          start={property.active.start}
                          finish={property.active.finish}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminMeterList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminMeterList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminMeterList,
  {
    viewer: graphql`
      fragment AdminMeterList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        meters(
          first: $count
          after: $cursor
        ) @connection(key: "AdminMeterList_meters") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              identifier
              tradePointId
              active {
                start
                finish
              }
              property {
                uuid
                title
                active {
                  start
                  finish
                }
              }
              primaryBillingPoint {
                uuid
                identifier
                active {
                  start
                  finish
                }
              }
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.meters;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminMeterList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminMeterList_viewer
      }
    }
  `,
  },
);
